import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  X,
  CreditCard,
  Coins,
  Copy,
  Loader2,
  CheckCircle2,
  Clock,
  ExternalLink,
  AlertCircle,
  Lock,
  ShieldCheck,
} from "lucide-react";

// Two-step crypto checkout:
//   step "method"  -> pick "card" or "crypto"
//   step "card"    -> on-ramp widget placeholder (destination wallet locked)
//   step "crypto"  -> wallet address + QR code
//   step "submit"  -> common TxID input + confirm button (after method chosen)
//   step "done"    -> pending confirmation screen
export default function CheckoutModal({ open, quantity, onClose, onSubmitted }) {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState("method"); // method | card | crypto | done
  const [txId, setTxId] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [result, setResult] = useState(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep("method");
    setTxId("");
    setNote("");
    setPaymentMethod(null);
    setResult(null);
    (async () => {
      try {
        const { data } = await api.get("/payments/crypto/config");
        setConfig(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [open]);

  const totalEur = useMemo(() => quantity * (config?.ticket_price_eur ?? 25), [quantity, config]);
  const usdtPerTicket = config?.usdt_per_ticket ?? 28;
  const totalUsdt = useMemo(() => quantity * usdtPerTicket, [quantity, usdtPerTicket]);

  const copyAddress = async () => {
    if (!config?.wallet_address) return;
    try {
      await navigator.clipboard.writeText(config.wallet_address);
      toast.success("Adresa kopirana");
    } catch {
      toast.error("Kopiranje nije uspelo");
    }
  };

  const submitTxId = async () => {
    if (!paymentMethod) {
      toast.error("Izaberite metod plaćanja");
      return;
    }
    const tx = txId.trim();
    if (tx.length < 4) {
      toast.error("Unesite ispravan Transaction Hash (TxID)");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/crypto/submit", {
        quantity,
        payment_method: paymentMethod,
        tx_id: tx,
        note: note.trim() || null,
      });
      setResult(data);
      setStep("done");
      onSubmitted?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const onramp = config?.onramp;
  const onrampUrl = useMemo(() => {
    if (!config || !onramp) return null;
    const w = config.wallet_address;
    if (onramp.provider === "transak") {
      const base = onramp.environment === "production"
        ? "https://global.transak.com"
        : "https://global-stg.transak.com";
      const params = new URLSearchParams({
        apiKey: onramp.api_key || "PLACEHOLDER_API_KEY",
        cryptoCurrencyCode: "USDT",
        network: "tron",
        walletAddress: w,
        disableWalletAddressForm: "true",
        defaultCryptoAmount: String(totalUsdt),
        cryptoAmount: String(totalUsdt),
        fiatCurrency: "EUR",
        themeColor: "22ff77",
        hideMenu: "true",
      });
      return `${base}/?${params.toString()}`;
    }
    if (onramp.provider === "moonpay") {
      const base = onramp.environment === "production"
        ? "https://buy.moonpay.com"
        : "https://buy-sandbox.moonpay.com";
      const params = new URLSearchParams({
        apiKey: onramp.api_key || "PLACEHOLDER_API_KEY",
        currencyCode: "usdt_trx",
        walletAddress: w,
        baseCurrencyCode: "eur",
        quoteCurrencyAmount: String(totalUsdt),
        showWalletAddressForm: "false",
      });
      return `${base}/?${params.toString()}`;
    }
    if (onramp.provider === "mercuryo") {
      const base = "https://exchange.mercuryo.io";
      const params = new URLSearchParams({
        widget_id: onramp.api_key || "PLACEHOLDER_API_KEY",
        currency: "USDT",
        network: "TRON",
        address: w,
        fix_currency: "true",
        fix_address: "true",
        fiat_currency: "EUR",
        amount: String(totalUsdt),
      });
      return `${base}/?${params.toString()}`;
    }
    return null;
  }, [config, onramp, totalUsdt]);

  if (!open) return null;

  return (
    <div
      data-testid="checkout-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="relative w-full max-w-2xl nasa-card my-6">
        <button
          ref={closeBtnRef}
          onClick={onClose}
          data-testid="checkout-close"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center border border-white/10 rounded-sm hover:border-[#ff7a18] hover:text-[#ff7a18] transition"
          aria-label="Zatvori"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-1">USDT TRC20 Checkout</div>
            <h2 className="font-display text-2xl md:text-3xl font-bold">
              Plaćanje · <span className="text-[#22ff77]">{quantity}× tiket</span>
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="border border-white/10 bg-black/40 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Vrednost · EUR</div>
                <div className="font-display text-2xl font-bold text-[#ff7a18] mt-1" data-testid="checkout-total-eur">
                  {totalEur.toLocaleString("sr-Latn-RS")} €
                </div>
                <div className="text-[9px] text-zinc-600 mt-1">
                  {quantity}× tiket · {quantity * (config?.tokens_per_ticket ?? 25)} tokena
                </div>
              </div>
              <div className="border border-[#22ff77]/40 bg-[#22ff77]/5 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-[#22ff77]">Zahtevani iznos · USDT (TRC20)</div>
                <div className="font-display text-2xl font-bold text-[#22ff77] mt-1" data-testid="checkout-total-usdt">
                  {totalUsdt} USDT
                </div>
                <div className="text-[9px] text-zinc-500 mt-1">
                  fiksno {usdtPerTicket} USDT × {quantity} tiket{quantity === 1 ? "" : "a"}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
              <Lock className="inline w-3 h-3 text-[#22ff77] mr-1" />
              Plaćanje se vrši isključivo u <strong className="text-[#22ff77]">USDT-u na TRC20 mreži</strong>. Cena je fiksna:
              <strong className="text-[#22ff77]"> {usdtPerTicket} USDT</strong> po tiketu (kursni buffer uračunat).
            </p>
          </div>

          {/* Step: choose method */}
          {step === "method" && (
            <div className="space-y-4" data-testid="checkout-step-method">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Izaberite metod plaćanja</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  data-testid="pay-card-button"
                  onClick={() => {
                    setPaymentMethod("card");
                    setStep("card");
                  }}
                  className="group p-5 border border-white/10 rounded-sm hover:border-[#22ff77] bg-black/40 text-left transition relative overflow-hidden"
                >
                  <CreditCard className="w-7 h-7 text-[#22ff77] mb-3" />
                  <div className="font-display text-lg font-bold">Plati Karticom</div>
                  <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                    Visa / Mastercard / Apple Pay — konvertuje se direktno u USDT na našu wallet adresu (on-ramp widget).
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#22ff77] mt-3">
                    Provider: {onramp?.provider || "Transak"}
                  </div>
                </button>

                <button
                  data-testid="pay-crypto-button"
                  onClick={() => {
                    setPaymentMethod("crypto");
                    setStep("crypto");
                  }}
                  className="group p-5 border border-white/10 rounded-sm hover:border-[#ff7a18] bg-black/40 text-left transition relative overflow-hidden"
                >
                  <Coins className="w-7 h-7 text-[#ff7a18] mb-3" />
                  <div className="font-display text-lg font-bold">Plati Kriptom</div>
                  <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                    Direktan USDT transfer iz vašeg kripto novčanika na našu TRC20 adresu. Skenirajte QR kod.
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#ff7a18] mt-3">USDT · TRC20</div>
                </button>
              </div>
            </div>
          )}

          {/* Step: card (on-ramp widget) */}
          {step === "card" && (
            <div className="space-y-4" data-testid="checkout-step-card">
              <button
                onClick={() => setStep("method")}
                className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-[#22ff77]"
              >
                ← Promeni metod
              </button>

              <div className="border border-[#22ff77]/30 bg-[#22ff77]/5 p-3 rounded-sm flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-[#22ff77] mt-0.5 shrink-0" />
                <div className="text-[11px] text-zinc-300 leading-relaxed">
                  Destinacija je <strong>zaključana</strong> na našu TRC20 wallet adresu. Vi unosite samo iznos sa svoje
                  kartice — on-ramp provider šalje USDT direktno nama.
                </div>
              </div>

              {!onramp?.configured ? (
                <div
                  data-testid="onramp-placeholder"
                  className="border border-[#22ff77]/20 bg-black/40 p-6 rounded-sm"
                >
                  <div className="font-display text-base font-bold text-white mb-3">Uputstvo za plaćanje karticom</div>
                  <div className="text-[12px] text-zinc-300 leading-relaxed space-y-2">
                    <p>1. Klikni dugme ispod — otvoriće se <strong className="text-[#22ff77]">Guardarian</strong>, nezavisna platforma za kupovinu kriptovaluta.</p>
                    <p>2. Na Guardianu kupi tačno <strong className="text-[#22ff77]">{totalUsdt} USDT</strong> i unesi ovu adresu kao destinaciju:</p>
                    <div className="bg-black border border-[#22ff77]/30 rounded-sm px-3 py-2 font-mono text-[11px] text-[#22ff77] break-all my-2 flex items-center justify-between gap-2">
                      <span className="break-all">{config?.wallet_address}</span>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(config?.wallet_address); toast.success("Adresa kopirana!"); }}
                        className="shrink-0 p-1.5 border border-[#22ff77]/40 rounded-sm hover:bg-[#22ff77]/10 transition-all"
                        title="Kopiraj adresu"
                      >
                        <Copy className="w-3 h-3 text-[#22ff77]" />
                      </button>
                    </div>
                    <p>3. <strong className="text-[#ff7a18]">OBAVEZNO:</strong> Koristi isključivo <strong>TRC20 (Tron)</strong> mrežu. Slanje putem druge mreže rezultira gubitkom sredstava.</p>
                    <p className="text-[10px] text-zinc-500 pt-1 border-t border-white/5">Guardarian je nezavisna platforma. NAŠA Myner Adria nije odgovorna za greške nastale pri unosu adrese ili izboru mreže od strane korisnika.</p>
                  </div>
                  <div className="mt-4 text-[11px] text-zinc-300 text-center">
                    Zahtevani iznos: <strong className="text-[#22ff77]" data-testid="card-required-usdt">{totalUsdt} USDT</strong>
                  </div>
                  <a
                    href={`https://guardarian.com/buy-usdt?to_amount=${totalUsdt}&to_network=TRX&to_wallet=${config?.wallet_address}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="transak-button"
                    className="inline-flex items-center justify-center gap-2 mt-4 w-full py-3 bg-[#22ff77] text-black font-bold uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 transition-all"
                  >
                    Plati karticom — {totalUsdt} USDT
                  </a>
                </div>
              ) : (
                <div className="border border-white/10 rounded-sm overflow-hidden bg-black">
                  <iframe
                    title="On-Ramp widget"
                    data-testid="onramp-iframe"
                    src={onrampUrl}
                    className="w-full"
                    style={{ height: 520, border: 0 }}
                    allow="accelerometer; autoplay; camera; gyroscope; payment"
                  />
                </div>
              )}

              <TxIdInput
                txId={txId}
                setTxId={setTxId}
                note={note}
                setNote={setNote}
                onSubmit={submitTxId}
                submitting={submitting}
              />
            </div>
          )}

          {/* Step: crypto direct */}
          {step === "crypto" && (
            <div className="space-y-4" data-testid="checkout-step-crypto">
              <button
                onClick={() => setStep("method")}
                className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-[#22ff77]"
              >
                ← Promeni metod
              </button>

              {config?.placeholder_address && (
                <div className="border border-[#ff7a18]/40 bg-[#ff7a18]/5 p-3 rounded-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-[#ff7a18] mt-0.5 shrink-0" />
                  <div className="text-[11px] text-zinc-300 leading-relaxed">
                    <strong>Placeholder adresa</strong> — administrator još nije uneo pravu TRC20 adresu. Nemojte slati USDT
                    na ovu vrednost.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-[200px,1fr] gap-4 items-start">
                <div className="border border-white/10 bg-white p-3 rounded-sm">
                  <img
                    src={config?.qr_url}
                    alt="USDT TRC20 QR"
                    data-testid="crypto-qr"
                    className="w-full h-auto"
                    style={{ imageRendering: "pixelated" }}
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Wallet adresa · TRC20</div>
                    <div className="mt-2 flex items-stretch gap-2">
                      <div
                        data-testid="crypto-wallet-address"
                        className="flex-1 font-mono text-[12px] md:text-sm break-all bg-black/60 border border-white/10 px-3 py-3 rounded-sm text-[#22ff77]"
                      >
                        {config?.wallet_address || "—"}
                      </div>
                      <button
                        onClick={copyAddress}
                        data-testid="copy-address-button"
                        className="px-3 border border-[#22ff77]/40 text-[#22ff77] hover:bg-[#22ff77]/10 rounded-sm"
                        aria-label="Kopiraj"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-400 space-y-1 leading-relaxed">
                    <div>
                      Pošaljite tačno <strong className="text-[#22ff77]" data-testid="crypto-required-usdt">{totalUsdt} USDT</strong> na ovu adresu putem
                      <strong className="text-[#ff7a18]"> TRC20 (Tron)</strong> mreže.
                    </div>
                    <div>
                      Slanje preko druge mreže (ERC20, BEP20…) ili manjeg iznosa može rezultirati gubitkom sredstava ili odbijanjem kupovine.
                    </div>
                  </div>
                </div>
              </div>

              <TxIdInput
                txId={txId}
                setTxId={setTxId}
                note={note}
                setNote={setNote}
                onSubmit={submitTxId}
                submitting={submitting}
              />
            </div>
          )}

          {/* Step: done */}
          {step === "done" && result && (
            <div className="space-y-4 text-center py-2" data-testid="checkout-step-done">
              <Clock className="w-14 h-14 text-[#ff7a18] mx-auto" />
              <h3 className="font-display text-2xl font-bold">
                Uplata je <span className="text-[#ff7a18]">Na čekanju</span>
              </h3>
              <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
                {result.message}
              </p>
              <div className="grid grid-cols-2 gap-3 max-w-md mx-auto text-left">
                <div className="border border-white/10 bg-black/40 p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Plaćeno</div>
                  <div className="font-display text-xl font-bold text-[#ff7a18] mt-1">{result.amount_eur} €</div>
                </div>
                <div className="border border-white/10 bg-black/40 p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">USDT iznos</div>
                  <div className="font-display text-xl font-bold text-[#22ff77] mt-1">{result.amount_usdt}</div>
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                TxID: <span className="text-zinc-300 font-mono break-all normal-case tracking-normal">{result.tx_id}</span>
              </div>
              <button
                onClick={onClose}
                data-testid="checkout-done-close"
                className="mt-3 px-6 py-3 bg-[#22ff77] text-black font-bold uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 glow-green"
              >
                Razumem · Zatvori
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TxIdInput({ txId, setTxId, note, setNote, onSubmit, submitting }) {
  return (
    <div className="border-t border-white/10 pt-4 space-y-3">
      <div>
        <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Transaction Hash (TxID)
        </label>
        <input
          data-testid="txid-input"
          value={txId}
          onChange={(e) => setTxId(e.target.value)}
          placeholder="npr. 9a3d8b2f7c1e..."
          autoComplete="off"
          className="mt-1 w-full bg-black border border-white/10 px-3 py-3 text-sm font-mono rounded-sm focus:outline-none focus:border-[#22ff77]"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Napomena (opcionalno)
        </label>
        <input
          data-testid="txid-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="npr. uplata sa Binance walleta"
          maxLength={500}
          className="mt-1 w-full bg-black border border-white/10 px-3 py-3 text-sm rounded-sm focus:outline-none focus:border-[#22ff77]"
        />
      </div>
      <button
        onClick={onSubmit}
        disabled={submitting}
        data-testid="confirm-payment-button"
        className="w-full flex items-center justify-center gap-2 bg-[#22ff77] text-black font-bold py-3.5 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 glow-green disabled:opacity-60"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        Potvrdi uplatu
      </button>
      <p className="text-[10px] text-zinc-500 leading-relaxed text-center">
        Nakon potvrde, status prelazi u <span className="text-[#ff7a18]">"Na čekanju"</span>. Administrator manuelno potvrđuje
        transakciju nakon što proveri priliv na blockchain-u. Tek tada se brojač mašine pomera i dobijate tokene.
      </p>
    </div>
  );
}
